import React, { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../header/Header";
import Axios from "axios";
import moment from "moment";
import * as XLSX from "xlsx";
import Select from "react-select";
import {
  LuCheckCircle2,
  LuClipboardList,
  LuDownload,
  LuTarget,
  LuUserX,
  LuUsers,
} from "react-icons/lu";
import { UiButton } from "../ui/ui";
import { ClassificationPill } from "../dashboard/WeeklyPriorities";
import "./ActionItems.css";
const config = require("../../config/config");

const DAY = "YYYY-MM-DD";

// The report sends "Other (details)" pre-formatted; split it back for the pill
const splitClassification = (value) => {
  const match = /^Other\s*\((.+)\)\s*$/i.exec(String(value || "").trim());
  return match
    ? { classification: "Other", other: match[1] }
    : { classification: value || "", other: "" };
};

const normName = (s) => String(s || "").trim().toLowerCase();

// Users without a name fall back to a readable version of their email
const displayName = (name, email) => {
  if (name && String(name).trim() && !String(name).includes("@")) return name;
  const source = String(name || email || "");
  if (!source.includes("@")) return source || "Unknown";
  return source
    .substring(0, source.lastIndexOf("@"))
    .split(/[._]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getInitials = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const weekKey = (start, end) =>
  `${moment(start).format(DAY)}|${moment(end).format(DAY)}`;

// A week is "past" once a newer Monday has started; the current and future
// weeks can still get plans, so they read as "not yet".
const isPastWeek = (week) =>
  moment(week.startDate).isBefore(moment().startOf("isoWeek"), "day");

const weekLabel = (start, end) => {
  const s = moment(start);
  const e = moment(end);
  return s.isSame(e, "month")
    ? `${s.format("D")} – ${e.format("D MMM YYYY")}`
    : `${s.format("D MMM")} – ${e.format("D MMM YYYY")}`;
};

// react-select themed to the shared design tokens
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 8,
    borderColor: state.isFocused ? "hsl(221 83% 53%)" : "hsl(220 13% 91%)",
    boxShadow: state.isFocused ? "0 0 0 3px hsl(221 83% 53% / 0.15)" : "none",
    "&:hover": { borderColor: "hsl(221 83% 53% / 0.5)" },
    fontSize: 14,
  }),
  valueContainer: (base) => ({ ...base, maxHeight: 76, overflowY: "auto" }),
  placeholder: (base) => ({ ...base, color: "hsl(215 16% 47%)" }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "hsl(221 83% 53% / 0.1)",
    borderRadius: 6,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "hsl(221 83% 45%)",
    fontWeight: 500,
    fontSize: 12,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "hsl(221 83% 45%)",
    borderRadius: 6,
    ":hover": { backgroundColor: "hsl(221 83% 53% / 0.2)" },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    backgroundColor: state.isFocused ? "hsl(210 40% 96%)" : "white",
    color: "hsl(222 47% 11%)",
  }),
  menu: (base) => ({ ...base, borderRadius: 8, zIndex: 30, overflow: "hidden" }),
};

const VIEWS = [
  { key: "all", label: "Everyone" },
  { key: "submitted", label: "Submitted" },
  { key: "missing", label: "Not submitted" },
];

const ActionItems = () => {
  const [weeks, setWeeks] = useState([]); // [{ key, startDate, endDate, label }]
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const [users, setUsers] = useState([]); // active team members
  const [reportRows, setReportRows] = useState([]);
  const [selectedPeople, setSelectedPeople] = useState([]);
  const [view, setView] = useState("all");
  const [loading, setLoading] = useState(true);

  // Weeks that have plans, newest first
  useEffect(() => {
    Axios.get(config.API_BASE + "/user/getActionItemsGroups")
      .then((response) => {
        const res = response.data;
        if (res.status && Array.isArray(res.data)) {
          const list = res.data.map((range) => ({
            key: weekKey(range.startDate, range.endDate),
            startDate: moment(range.startDate).format(DAY),
            endDate: moment(range.endDate).format(DAY),
            label: weekLabel(range.startDate, range.endDate),
          }));
          setWeeks(list);
          if (list[0]) setSelectedWeeks([list[0]]);
          else setLoading(false);
        } else {
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching action group data:", error);
        setLoading(false);
      });

    Axios.get(config.API_BASE + "/user/getUserList", {
      params: { ifActive: 1 },
    })
      .then((response) => {
        const res = response.data;
        if (res.status && Array.isArray(res.data)) setUsers(res.data);
      })
      .catch((error) => console.error("Error fetching users:", error));
  }, []);

  const getReport = useCallback(() => {
    if (selectedWeeks.length === 0) {
      setReportRows([]);
      return;
    }
    setLoading(true);
    Axios.post(config.API_BASE + "/user/getActionItemsStatusReport", {
      dateBetween: selectedWeeks.map((w) => `${w.startDate}|${w.endDate}`),
    })
      .then((response) => {
        const res = response.data;
        setReportRows(res.status && Array.isArray(res.data) ? res.data : []);
      })
      .catch((error) => {
        console.error("Error fetching report data:", error);
        setReportRows([]);
      })
      .finally(() => setLoading(false));
  }, [selectedWeeks]);

  useEffect(() => {
    getReport();
  }, [getReport]);

  // Everyone on the team (active users) plus anyone who has plans in range,
  // each with their priorities grouped by week.
  const people = useMemo(() => {
    const byKey = new Map();
    const keyFor = (userId, name) =>
      userId != null ? `id:${userId}` : `name:${normName(name)}`;

    users.forEach((u) => {
      byKey.set(`id:${u.id}`, {
        key: `id:${u.id}`,
        userId: u.id,
        name: displayName(u.name, u.email),
        email: u.email,
        items: [],
      });
    });
    // Older API responses have no userId, so fall back to matching on name
    const idByName = new Map(
      users.map((u) => [normName(u.name), `id:${u.id}`]),
    );

    reportRows.forEach((row) => {
      let key =
        row.userId != null
          ? keyFor(row.userId)
          : idByName.get(normName(row.name)) || keyFor(null, row.name);
      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          userId: row.userId,
          name: displayName(row.name, row.email),
          email: row.email || "",
          items: [],
        });
      }
      const [start, end] = String(row.period || "").split(" - ");
      const { classification, other } = splitClassification(row.classification);
      byKey.get(key).items.push({
        actionItem: row.actionItems || "",
        classification,
        other,
        status: row.status ? String(row.status).trim() : "",
        week: start && end ? weekKey(start, end) : "",
      });
    });

    return [...byKey.values()].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );
  }, [users, reportRows]);

  const filteredPeople = useMemo(() => {
    if (selectedPeople.length === 0) return people;
    const wanted = new Set(selectedPeople.map((p) => p.value));
    return people.filter((p) => wanted.has(p.key));
  }, [people, selectedPeople]);

  const submitted = filteredPeople.filter((p) => p.items.length > 0);
  // Per selected week, who hasn't planned anything (only active team members)
  const missingByWeek = selectedWeeks.map((w) => ({
    week: w,
    people: filteredPeople.filter(
      (p) =>
        p.userId != null &&
        users.some((u) => u.id === p.userId) &&
        !p.items.some((it) => it.week === w.key),
    ),
  }));
  const missingCount = new Set(
    missingByWeek.flatMap((m) => m.people.map((p) => p.key)),
  ).size;
  const teamSize = filteredPeople.filter((p) =>
    users.some((u) => u.id === p.userId),
  ).length;
  const totalItems = submitted.reduce((n, p) => n + p.items.length, 0);
  const actualsDone = submitted.reduce(
    (n, p) => n + p.items.filter((it) => it.status).length,
    0,
  );
  const submittedPct = teamSize
    ? Math.round(((teamSize - missingCount) / teamSize) * 100)
    : 0;

  const exportToExcel = () => {
    const rows = [];
    submitted.forEach((p) =>
      p.items.forEach((it) => {
        const [start, end] = it.week.split("|");
        rows.push({
          Name: p.name,
          Email: p.email || "",
          ActionItem: it.actionItem,
          Classification:
            it.classification === "Other" && it.other
              ? `Other (${it.other})`
              : it.classification,
          Status: it.status || "Not Available",
          Period: start ? `${start} - ${end}` : "",
        });
      }),
    );
    missingByWeek.forEach(({ week, people: list }) =>
      list.forEach((p) =>
        rows.push({
          Name: p.name,
          Email: p.email || "",
          ActionItem: "No plan submitted",
          Classification: "",
          Status: "",
          Period: `${week.startDate} - ${week.endDate}`,
        }),
      ),
    );
    if (rows.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Weekly Plans");
    XLSX.writeFile(wb, "Weekly_Plans.xlsx");
  };

  const weekOptions = weeks.filter(
    (w) => !selectedWeeks.some((s) => s.key === w.key),
  );
  const peopleOptions = people.map((p) => ({ label: p.name, value: p.key }));
  const multiWeek = selectedWeeks.length > 1;
  const weeksWithMissing = missingByWeek.filter((m) => m.people.length > 0);
  const allOpenWeeks = weeksWithMissing.every((m) => !isPastWeek(m.week));

  return (
    <>
      <Header />
      <div className="container-fluid">
        <div className="tw-mx-auto tw-max-w-7xl tw-py-4 tw-font-sans tw-text-foreground sm:tw-py-6">
          <div className="tw-mb-6 tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-3">
            <div>
              <h1 className="tw-m-0 tw-text-2xl tw-font-semibold tw-leading-8 tw-text-foreground">
                Weekly Plans
              </h1>
              <div className="tw-mt-1 tw-text-sm tw-text-muted-foreground">
                Everyone's priorities for the week, and who still needs to
                submit theirs.
              </div>
            </div>
            <UiButton
              variant="outline"
              onClick={exportToExcel}
              disabled={loading || filteredPeople.length === 0}
            >
              <LuDownload aria-hidden="true" />
              Export to Excel
            </UiButton>
          </div>

          {/* Filters */}
          <div className="tw-mb-6 tw-grid tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-border/60 tw-bg-card tw-p-4 tw-shadow-soft md:tw-grid-cols-2">
            <div>
              <div className="tw-mb-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted-foreground">
                Weeks
              </div>
              <Select
                isMulti
                styles={selectStyles}
                value={selectedWeeks}
                options={weekOptions}
                getOptionValue={(o) => o.key}
                getOptionLabel={(o) => o.label}
                onChange={(opts) => setSelectedWeeks(opts || [])}
                closeMenuOnSelect={false}
                placeholder="Select weeks"
              />
            </div>
            <div>
              <div className="tw-mb-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted-foreground">
                People
              </div>
              <Select
                isMulti
                styles={selectStyles}
                value={selectedPeople}
                options={peopleOptions}
                onChange={(opts) => setSelectedPeople(opts || [])}
                closeMenuOnSelect={false}
                placeholder="Everyone"
              />
            </div>
          </div>

          {/* Stats */}
          <div className="tw-mb-6 tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-gap-4 lg:tw-grid-cols-4">
            <Stat
              icon={LuUsers}
              tone="tw-bg-primary/10 tw-text-primary"
              label="Team members"
              value={teamSize}
            />
            <Stat
              icon={LuCheckCircle2}
              tone="tw-bg-emerald-100 tw-text-emerald-700"
              label="Plans submitted"
              value={`${Math.max(teamSize - missingCount, 0)}`}
              suffix={`/ ${teamSize}`}
              progress={submittedPct}
            />
            <Stat
              icon={LuUserX}
              tone="tw-bg-amber-100 tw-text-amber-700"
              label="Not submitted"
              value={missingCount}
            />
            <Stat
              icon={LuTarget}
              tone="tw-bg-violet-100 tw-text-violet-700"
              label="Priorities · actuals in"
              value={totalItems}
              suffix={`· ${actualsDone} updated`}
            />
          </div>

          {/* View switch */}
          <div
            className="tw-mb-4 tw-inline-flex tw-gap-1 tw-rounded-lg tw-bg-white tw-p-1 tw-shadow-soft"
            role="tablist"
            aria-label="Filter people"
          >
            {VIEWS.map((v) => {
              const count =
                v.key === "submitted"
                  ? submitted.length
                  : v.key === "missing"
                    ? missingCount
                    : null;
              return (
                <button
                  key={v.key}
                  type="button"
                  role="tab"
                  aria-selected={view === v.key}
                  onClick={() => setView(v.key)}
                  className={`tw-inline-flex tw-h-8 tw-items-center tw-gap-1.5 tw-rounded-md tw-border-0 tw-px-3 tw-text-sm tw-font-medium tw-transition-colors ${
                    view === v.key
                      ? "tw-bg-primary/10 tw-text-primary"
                      : "tw-bg-transparent tw-text-muted-foreground hover:tw-text-foreground"
                  }`}
                >
                  {v.label}
                  {count !== null && (
                    <span className="tw-rounded-full tw-bg-secondary tw-px-1.5 tw-text-[11px] tw-font-semibold tw-text-muted-foreground">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="tw-grid tw-gap-4 xl:tw-grid-cols-2">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="tw-h-48 tw-animate-pulse tw-rounded-lg tw-bg-white/70"
                />
              ))}
            </div>
          ) : selectedWeeks.length === 0 ? (
            <EmptyState text="Select a week to see the team's plans." />
          ) : (
            <div className="tw-space-y-6">
              {view !== "submitted" && missingCount > 0 && (
                <section className="tw-rounded-lg tw-border tw-border-solid tw-border-amber-200 tw-bg-card tw-shadow-soft">
                  <div className="tw-flex tw-items-center tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-amber-100 tw-bg-amber-50/60 tw-px-5 tw-py-3">
                    <div className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-bg-amber-100 tw-text-amber-700">
                      <LuUserX aria-hidden="true" />
                    </div>
                    <div>
                      <h2 className="tw-m-0 tw-text-base tw-font-semibold tw-text-foreground">
                        {allOpenWeeks
                          ? "No weekly plan yet"
                          : multiWeek
                            ? "Weekly plans not submitted"
                            : "Weekly plan not submitted"}
                      </h2>
                      <div className="tw-text-xs tw-text-muted-foreground">
                        {missingCount}{" "}
                        {missingCount === 1 ? "person" : "people"}{" "}
                        {allOpenWeeks
                          ? "haven't submitted priorities yet"
                          : "didn't submit priorities"}{" "}
                        {multiWeek ? (
                          `across ${selectedWeeks.length} weeks`
                        ) : (
                          <>
                            for{" "}
                            <span className="tw-font-semibold tw-text-foreground">
                              {selectedWeeks[0].label}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="tw-space-y-4 tw-p-5">
                    {missingByWeek
                      .filter((m) => m.people.length > 0)
                      .map(({ week, people: list }) => (
                        <div key={week.key}>
                          {multiWeek && (
                            <div className="tw-mb-2 tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted-foreground">
                              Week of {week.label}
                              <span className="tw-rounded-full tw-bg-amber-100 tw-px-1.5 tw-py-px tw-text-[11px] tw-normal-case tw-tracking-normal tw-text-amber-800">
                                {list.length}{" "}
                                {isPastWeek(week) ? "didn't submit" : "not submitted yet"}
                              </span>
                            </div>
                          )}
                          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-2 tw-p-0">
                            {list.map((p) => (
                              <li
                                key={p.key}
                                className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-solid tw-border-border tw-bg-white tw-py-1 tw-pl-1 tw-pr-3"
                                title={p.email || p.name}
                              >
                                <Avatar name={p.name} muted />
                                <span className="tw-text-sm tw-font-medium tw-text-foreground">
                                  {p.name}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                  </div>
                </section>
              )}

              {view !== "missing" &&
                (submitted.length === 0 ? (
                  <EmptyState text="No plans submitted for the selected weeks." />
                ) : (
                  <div className="tw-grid tw-items-start tw-gap-4 xl:tw-grid-cols-2">
                    {submitted.map((p) => (
                      <PersonCard
                        key={p.key}
                        person={p}
                        weeks={selectedWeeks}
                        multiWeek={multiWeek}
                      />
                    ))}
                  </div>
                ))}

              {view === "missing" && missingCount === 0 && (
                <EmptyState text="Everyone has submitted their plan. 🎉" />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const Avatar = ({ name, muted = false }) => (
  <span
    className={`tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-select-none tw-items-center tw-justify-center tw-rounded-full tw-text-[11px] tw-font-semibold ${
      muted
        ? "tw-bg-secondary tw-text-muted-foreground"
        : "tw-bg-gradient-primary tw-text-white"
    }`}
    aria-hidden="true"
  >
    {getInitials(name)}
  </span>
);

const Stat = ({ icon: Icon, tone, label, value, suffix, progress }) => (
  <div className="tw-rounded-lg tw-border tw-border-solid tw-border-border/60 tw-bg-card tw-p-4 tw-shadow-soft sm:tw-p-5">
    <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
      <div className="tw-text-sm tw-font-medium tw-text-muted-foreground">
        {label}
      </div>
      <div
        className={`tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg ${tone}`}
      >
        <Icon aria-hidden="true" />
      </div>
    </div>
    <div className="tw-mt-2 tw-flex tw-items-baseline tw-gap-1.5">
      <span className="tw-text-3xl tw-font-semibold tw-leading-9 tw-text-foreground">
        {value}
      </span>
      {suffix && (
        <span className="tw-text-sm tw-text-muted-foreground">{suffix}</span>
      )}
    </div>
    {typeof progress === "number" && (
      <div className="tw-mt-3 tw-h-1.5 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-secondary">
        <div
          className="tw-h-full tw-rounded-full tw-bg-success"
          style={{ width: `${progress}%` }}
        />
      </div>
    )}
  </div>
);

const EmptyState = ({ text }) => (
  <div className="tw-flex tw-flex-col tw-items-center tw-rounded-lg tw-border tw-border-dashed tw-border-border tw-bg-white tw-px-4 tw-py-12 tw-text-center">
    <LuClipboardList
      className="tw-mb-2 tw-text-2xl tw-text-muted-foreground"
      aria-hidden="true"
    />
    <div className="tw-text-sm tw-text-muted-foreground">{text}</div>
  </div>
);

const PersonCard = ({ person, weeks, multiWeek }) => {
  const done = person.items.filter((it) => it.status).length;
  const groups = multiWeek
    ? weeks
        .map((w) => ({
          week: w,
          items: person.items.filter((it) => it.week === w.key),
        }))
        .filter((g) => g.items.length > 0)
    : [{ week: null, items: person.items }];

  return (
    <section className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-border/60 tw-bg-card tw-shadow-soft">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-border tw-px-5 tw-py-3.5">
        <Avatar name={person.name} />
        <div className="tw-min-w-[160px] tw-flex-1">
          <h2 className="tw-m-0 tw-truncate tw-text-base tw-font-semibold tw-leading-6 tw-text-foreground">
            {person.name}
          </h2>
          {person.email && (
            <div className="tw-truncate tw-text-xs tw-text-muted-foreground">
              {person.email}
            </div>
          )}
        </div>
        <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1.5">
          <span className="tw-rounded-full tw-bg-primary/10 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-primary">
            {person.items.length}{" "}
            {person.items.length === 1 ? "priority" : "priorities"}
          </span>
          <span
            className={`tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold ${
              done === person.items.length
                ? "tw-bg-emerald-50 tw-text-emerald-700"
                : "tw-bg-secondary tw-text-muted-foreground"
            }`}
            title="Actuals updated"
          >
            {done}/{person.items.length} actuals
          </span>
        </div>
      </div>
      {groups.map((g) => (
        <div key={g.week ? g.week.key : "all"}>
          {g.week && (
            <div className="tw-bg-secondary/60 tw-px-5 tw-py-1.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted-foreground">
              {g.week.label}
            </div>
          )}
          <ol className="tw-m-0 tw-list-none tw-divide-y tw-divide-border tw-p-0">
            {g.items.map((it, i) => (
              <li key={i} className="tw-flex tw-gap-3 tw-px-5 tw-py-3">
                <span
                  className={`tw-mt-0.5 tw-flex tw-h-5 tw-w-5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-[11px] tw-font-semibold ${
                    it.status
                      ? "tw-bg-emerald-100 tw-text-emerald-700"
                      : "tw-bg-primary/10 tw-text-primary"
                  }`}
                >
                  {i + 1}
                </span>
                <div className="tw-min-w-0 tw-flex-1">
                  <div className="tw-break-words tw-text-sm tw-leading-5 tw-text-foreground">
                    {it.actionItem}
                  </div>
                  <div className="tw-mt-1.5 tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1">
                    {it.classification && (
                      <ClassificationPill
                        classification={it.classification}
                        other={it.other}
                      />
                    )}
                    {it.status ? (
                      <span className="tw-break-words tw-text-xs tw-text-foreground">
                        <span className="tw-font-semibold tw-text-muted-foreground">
                          Actual:{" "}
                        </span>
                        {it.status}
                      </span>
                    ) : (
                      <span className="tw-text-xs tw-italic tw-text-muted-foreground">
                        Actual pending
                      </span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      ))}
    </section>
  );
};

export default ActionItems;
